import React from 'react';
import { Stack, Title, Text, Button, Paper, ThemeIcon } from '@mantine/core';
import { IconBug } from '@tabler/icons-react';

interface ErrorBoundaryProps {
    children: React.ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

/**
 * Root-level error boundary that catches unhandled React errors
 * and shows a recovery UI instead of a blank screen.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null });
    };

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return (
                <Stack align="center" justify="center" mih="100vh" p="xl">
                    <Paper shadow="md" radius="lg" p="xl" maw={520} w="100%">
                        <Stack align="center" gap="md">
                            <ThemeIcon variant="light" color="red" size={64} radius="xl">
                                <IconBug size={32} stroke={1.5} />
                            </ThemeIcon>
                            <Title order={3} ta="center">Something went wrong</Title>
                            <Text size="sm" c="dimmed" ta="center">
                                An unexpected error occurred. You can try recovering or reload the page.
                            </Text>
                            {this.state.error && (
                                <Text size="xs" c="dimmed" ff="monospace" ta="center" style={{ wordBreak: 'break-word' }}>
                                    {this.state.error.message}
                                </Text>
                            )}
                            <Stack gap="xs" w="100%">
                                <Button onClick={this.handleReset} fullWidth>
                                    Try to recover
                                </Button>
                                <Button variant="light" onClick={this.handleReload} fullWidth>
                                    Reload page
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>
                </Stack>
            );
        }

        return this.props.children;
    }
}
